/** @jsx jsx */
import { Box, Flex, jsx, Select, Text } from "theme-ui";
import React, { Fragment, PropsWithChildren, useEffect, useState } from "react";
import { StopEvent } from "./trip-planner-response.interface";
import { graphql, useStaticQuery } from "gatsby";

import { BuilderBlocks } from "@builder.io/react";
import { BuilderBlock } from "@builder.io/react/dist/types/src/components/builder-block.component";

export type departureBoardProps = {
  disabled: boolean;
  builderBlock: BuilderBlock;
} & PropsWithChildren;
export const DepartureBoard: React.FC<departureBoardProps> = ({
  disabled = false,
  children,
  builderBlock,
}) => {
  const { departurePoints } =
    useStaticQuery<Queries.departureBoardStopsQuery>(query).allBuilderModels;

  const [stopEvents, setStopEvents] = useState<StopEvent[] | null>(null);
  const [currentStop, setCurrentStop] = useState<string | null>(null);

  const departureBoard = departurePoints ? departurePoints[0]?.data : null;
  const stops = departureBoard?.stops;

  // const departurePoints: { id: string; name: string; default: boolean }[] =
  //   rockitwebSettings.departureBoard.departurePoints;
  let def = stops.find((d) => d.default === true);
  if (!def) def = stops[0];

  function getStops() {
    if (!currentStop) {
      setCurrentStop(def.id);
    }

    if (!currentStop) return;

    setStopEvents(null); //removes annoying paint error

    fetch(`/api/trip-planner?from=${currentStop}`)
      .then((response) => response.json()) // parse JSON from request
      .then((resultData: StopEvent[]) => {
        setStopEvents(resultData);
      });
  }

  function stopChanged(event: any) {
    setCurrentStop(event.target.value);
  }

  useEffect(() => {
    if (disabled) return;
    getStops();
  }, [currentStop]);

  useEffect(() => {
    if (disabled) return;
    const interval = setInterval(() => {
      getStops();
    }, 60000);
    return () => {
      clearInterval(interval);
    };
  });

  return (
    <Box sx={{ height: "100%" }}>
      {disabled ? (
        <Fragment>
          <BuilderBlocks
            sx={{ display: "inline", width: "100%" }}
            child
            parentElementId={builderBlock?.id}
            dataPath="this.children"
            blocks={children}
          />
        </Fragment>
      ) : (
        <Box>
          <Flex
            p={1}
            sx={{
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <label
              htmlFor="selectStation"
              sx={{
                width: "20%",
                fontWeight: "bold",
                textAlign: "center",
                color: "primary",
              }}
            >
              FROM
            </label>
            <Box sx={{ width: "80%" }}>
              <Select
                id="selectStation"
                onChange={stopChanged}
                defaultValue="10101331"
                name="select from Station"
                aria-label="Select from Station"
              >
                {stops.map((station: any, i: number) => (
                  <option key={`${station.id}`} value={station.id}>
                    {station.name}
                  </option>
                ))}
              </Select>
            </Box>
          </Flex>
          {stopEvents && (
            <Flex
              m={0}
              p={0}
              as="ul"
              sx={{
                height: "300px",
                overflow: "auto",
                flexDirection: "column",
                textIndent: 0,
                listStyleType: "none",
              }}
            >
              {stopEvents
                .splice(0, 100)
                .sort((a, b) => {
                  const aDate = new Date(
                    a.departureTimeEstimated || a.departureTimePlanned
                  );
                  const bDate = new Date(
                    b.departureTimeEstimated || b.departureTimePlanned
                  );
                  return aDate.getTime() - bDate.getTime();
                })
                .map((stop, i) => {
                  const {
                    location,
                    departureTimePlanned,
                    departureTimeEstimated,
                    transportation,
                  } = stop;
                  const now = new Date().getTime();
                  const departureTime = new Date(
                    departureTimeEstimated || departureTimePlanned
                  ).getTime();
                  const minutesToDeparture = Math.floor(
                    (departureTime - now) / 1000 / 60
                  );

                  if (minutesToDeparture < 0) return null;
                  return (
                    <Flex
                      key={`${location.id}-${i}`}
                      as="li"
                      m={0}
                      p={0}
                      sx={{
                        textIndent: 0,
                        listStyleType: "none",
                        borderTopColor: "primary",
                        borderTopWidth: "1px",
                        borderTopStyle: "solid",
                        borderRightColor: "white",
                      }}
                    >
                      <Flex
                        bg="primaryDark"
                        color="white"
                        sx={{
                          width: "20%",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >{`${minutesToDeparture} min`}</Flex>
                      <Flex
                        p={1}
                        sx={{ width: "80%", flexDirection: "column" }}
                      >
                        <Box sx={{ fontSize: 0 }}>{location.name}</Box>
                        <Box>
                          <Text
                            color="primary"
                            sx={{ fontSize: 1 }}
                          >{`${transportation.number} to ${transportation.destination.name}`}</Text>
                        </Box>
                      </Flex>
                    </Flex>
                  );
                })}
            </Flex>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DepartureBoard;
const query = graphql`
  query departureBoardStops {
    allBuilderModels {
      departurePoints {
        data {
          stops
        }
      }
    }
  }
`;
